import { AccentBlock, Alert, Breadcrumb, Breadcrumbs, DisplayItem, Heading, Text } from '@digitools/honest-ui';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useTranslate from 'src/hooks/useTranslate';
import ConfirmationText from './confirmationText';
import useAsDataContext from '../../hooks/useAsDataContext';
import { useNavigate } from 'react-router-dom';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import useTealium from 'src/hooks/useTealium';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import DownloadAndPrint from './DownloadPdf';
import useAsContext from '../../hooks/useAsConfigContext';
import { trackBreadcrumbEvent, useHomePageBreadcrumbs } from 'src/components/Breadcrumbs';
import { AsText } from '../../constants/Text';
// Claimant name and today's date side by side; they wrap onto separate lines on narrow screens
const SummaryFields = styled('div') `
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md) var(--spacing-xl-8);
`;
const NextSteps = styled('ol') `
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
`;
// Figma: Paragraph Text/Large/Bold (18/28) and Description/Regular (16/20), both --color-text-primary
// Set explicitly (not only via Text size/weight) so unlayered app styles can't override the Figma values
const StepTitle = styled(Text) `
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-xl);
  color: var(--color-text-primary);
`;
const StepDescription = styled(Text) `
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-xs);
  color: var(--color-text-primary);
`;
const NEXT_STEPS = [
    { tone: 'light', heading: ConfirmationText.firstStepHeading, description: ConfirmationText.firstStepDesc },
    { tone: 'medium', heading: ConfirmationText.secondStepHeading, description: ConfirmationText.secondStepDesc },
    { tone: 'dark', heading: ConfirmationText.thirdStepHeading, description: ConfirmationText.thirdStepDesc },
];
const Confirmation = () => {
    const { t } = useTranslate();
    let navigate = useNavigate();
    const { experience } = useExperience();
    const { claimantData, accommodationResponse } = useAsDataContext();
    const { trackView, trackEvent } = useTealium();
    const [downloadError, setDownloadError] = useState();
    const [isSuccessOpen, setIsSuccessOpen] = useState(true);
    const { asConfig } = useAsContext();
    const headingRef = useRef(null);
    useEffect(() => {
        window.scrollTo(0, 0);
        headingRef.current?.focus();
        if (accommodationResponse === undefined) {
            if (experience === Experience.EMPLOYEE) {
                navigate('/as/employee');
            }
            else if (experience === Experience.EMPLOYER) {
                navigate('/as/employer');
            }
        }
        trackView({
            page_l3: TEALIUM_EVENT.EVENT_TYPE.ACCOMMODATION,
            page_l5: 'confirmation'
        });
    }, []);
    const to = experience === Experience.EMPLOYER ? '/as/employer/intake' : '/as/employee/intake';
    return (<div ref={headingRef} tabIndex={-1} aria-live='polite' aria-label={`Success ${t(ConfirmationText.pageTitle)}`}>
      <Breadcrumbs>
        {useHomePageBreadcrumbs()}
        <Breadcrumb onClick={() => trackBreadcrumbEvent(t(AsText.intakeHeadingAccommodationCustomer), trackEvent)} href={to}>{t(AsText.intakeHeadingAccommodationCustomer)}</Breadcrumb>
        <Breadcrumb>{t(AsText.confirmation)}</Breadcrumb>
      </Breadcrumbs>
      <Heading as='h1' visualLevel='h2' className='hui-mt-lg hui-mb-xl' data-public={true} data-testid='confirmation-title-heading'>
        <span data-testid='confirmation-title-heading-accommodation'>{t(ConfirmationText.pageTitle)}</span>
      </Heading>

      {/* Both alerts stay rendered and are toggled with isOpen */}
      <Alert type='error' dismissible isOpen={!!downloadError} onOpenChange={() => setDownloadError(undefined)} className='hui-mb-lg' data-testid='confirmation-download-error'>
        {downloadError}
      </Alert>
      <Alert type='success' dismissible isOpen={isSuccessOpen} onOpenChange={setIsSuccessOpen} className='hui-mb-xl' data-public={true} data-testid='employeeMessage'>
        {t(ConfirmationText.successMsg)}
      </Alert>

      <AccentBlock className='hui-mb-xl' data-public={true}>
        <Heading as='h2' visualLevel='h3' weight='bold' color='secondary' className='hui-mb-md' data-testid='eventNum'>
          {t(ConfirmationText.requestId)}: #{accommodationResponse?.eventNum}
        </Heading>
        <SummaryFields className='hui-mb-lg'>
          <DisplayItem label={t(ConfirmationText.name)} value={<span data-private={true}>{claimantData?.empFirstName} {claimantData?.empLastName}</span>} data-testid='empFirstName'/>
          <DisplayItem label={t(ConfirmationText.date)} value={`${accommodationResponse?.submittedDate?.replace(/\-/g, '/') ?? ''} at ${accommodationResponse?.submittedTime ?? ''}`}/>
        </SummaryFields>
        <div data-testid='downloadAndPrint'>
          <DownloadAndPrint setErrorMessage={setDownloadError}/>
        </div>
      </AccentBlock>

      {asConfig?.config?.customMessageConfirmation &&
            <div className='hui-mb-xl'>
          {t(asConfig.config.customMessageConfirmation)}
        </div>}

      <Heading as='h2' visualLevel='h3' weight='bold' color='secondary' className='hui-mb-md' data-public={true} data-testid='next-steps'>
        {t(ConfirmationText.nextStepText)}
      </Heading>
      <NextSteps aria-label={t(ConfirmationText.nextStepText)} data-public={true}>
        {NEXT_STEPS.map((step, index) => (<AccentBlock key={index} as='li' number={index + 1} tone={step.tone}>
            <StepTitle as='p' size='lg' weight='bold'>{t(step.heading)}</StepTitle>
            <StepDescription as='p' size='md'>{t(step.description)}</StepDescription>
          </AccentBlock>))}
      </NextSteps>
    </div>);
};
export default Confirmation;
